/**
 * "Continue with ..." provider buttons (AB#333).
 *
 * Renders nothing until the backend reports social login enabled
 * (Supabase GoTrue env configured). Clicking a provider sends the browser to
 * GoTrue's authorize endpoint; GoTrue redirects back to /login with the
 * session in the URL hash, which the login page exchanges for an app session.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { API_BASE_BY_ENV, activeEnv, authApi, isTauri, openExternal } from '@wordpuppi/shared';
import { Button } from '@/components/ui/button';
import { buildAuthorizeUrl } from '@/lib/socialAuth';

// "Continue with X" is the neutral call-to-action sanctioned across the four
// providers' branding guidelines (Google: "Sign in / Sign up / Continue with
// Google"; Apple HIG; Meta) and — unlike "Sign in with X" — reads correctly on
// BOTH the login and register pages that share this component. (Microsoft's spec
// prefers "Sign in with Microsoft"; "Continue with" is the shared-CTA tradeoff.)
const PROVIDER_LABELS: Record<string, string> = {
  google: 'Continue with Google',
  facebook: 'Continue with Facebook',
  azure: 'Continue with Microsoft',
  apple: 'Continue with Apple',
};

/**
 * Official provider marks as retina-crisp inline SVGs. Colors audited against
 * each provider's current brand guideline: Google's standard four-color "G"
 * (#EA4335/#4285F4/#FBBC05/#34A853 — must not be monochromed), Microsoft's
 * four-square logo (#F25022/#7FBA00/#00A4EF/#FFB900), Facebook's "f" on brand
 * blue, and Apple's monochrome mark (currentColor, matching the label).
 */
const PROVIDER_ICONS: Record<string, ReactNode> = {
  // Google "G" in official brand colors
  google: (
    <svg className="size-[18px]" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  ),
  // Facebook "f" on the official #1877F2 circle
  facebook: (
    <svg className="size-[18px]" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#1877F2"
        d="M24 12c0-6.627-5.373-12-12-12S0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.234 2.686.234v2.953h-1.514c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12z"
      />
      <path
        fill="#fff"
        d="M16.671 15.469L17.203 12h-3.328V9.749c0-.949.465-1.874 1.956-1.874h1.514V4.922s-1.374-.234-2.686-.234c-2.741 0-4.533 1.66-4.533 4.668V12H7.078v3.469h3.047v8.385a12.137 12.137 0 0 0 3.75 0v-8.385h2.796z"
      />
    </svg>
  ),
  // Microsoft four-square logo in official brand colors
  azure: (
    <svg className="size-[18px]" viewBox="0 0 23 23" aria-hidden="true">
      <path fill="#F25022" d="M1 1h10v10H1z" />
      <path fill="#7FBA00" d="M12 1h10v10H12z" />
      <path fill="#00A4EF" d="M1 12h10v10H1z" />
      <path fill="#FFB900" d="M12 12h10v10H12z" />
    </svg>
  ),
  // Apple logo, monochrome — matches the label color per Apple guidelines
  apple: (
    <svg className="size-[18px]" viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
      />
    </svg>
  ),
};

export function SocialLoginButtons() {
  const [status, setStatus] = useState<{ enabled: boolean; url: string; providers: string[] } | null>(
    null,
  );

  useEffect(() => {
    let cancelled = false;
    authApi
      .socialStatus()
      .then((res) => {
        if (!cancelled && res.data) setStatus(res.data);
      })
      .catch(() => {
        // Feature stays hidden if the status probe fails.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!status?.enabled) return null;

  const startLogin = (provider: string) => {
    if (isTauri() && activeEnv) {
      // Desktop: GoTrue's URI allow-list does NOT contain the `wordpuppi://`
      // custom scheme, so redirecting there makes GoTrue silently fall back to
      // SITE_URL (app.wordpuppi.com) and strand the system browser. Instead send
      // it to the ALREADY-ALLOW-LISTED https app origin at `/login?wpp_desktop=1`;
      // that web page recognizes the marker and bounces the fragment back into
      // the app via `wordpuppi://auth/social#...` (shell.ts's deep-link consumer).
      // wry blocks in-webview OAuth navigations, so open in the real browser.
      // appWebOrigin = the env's API base with a trailing `/api` stripped
      // (prod -> https://app.wordpuppi.com). (AB#333)
      const appWebOrigin = API_BASE_BY_ENV[activeEnv].replace(/\/api$/, '');
      void openExternal(
        buildAuthorizeUrl(status.url, provider, `${appWebOrigin}/login?wpp_desktop=1`),
      );
      return;
    }
    // Web: return to the /login route (the only page that exchanges the hash
    // token), built off the app basepath (BASE_URL is `/` since AB#447 dropped
    // the /admin basepath) so GoTrue's redirect lands on a real SPA route. (AB#333)
    window.location.href = buildAuthorizeUrl(
      status.url,
      provider,
      `${window.location.origin}${import.meta.env.BASE_URL}login`,
    );
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">or</span>
        </div>
      </div>
      <div className="grid gap-2">
        {status.providers.map((provider) => (
          <Button
            key={provider}
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => startLogin(provider)}
          >
            {PROVIDER_ICONS[provider]}
            {PROVIDER_LABELS[provider] ?? provider}
          </Button>
        ))}
      </div>
    </div>
  );
}
